/**
 * StateManager - Singleton for managing global app state with caching and notifications
 *
 * Caches:
 * - User info
 * - Chat messages
 * - Todos
 * - Progress data
 * - Conversation ID
 *
 * Features:
 * - Observer pattern for component subscriptions
 * - Automatic cache invalidation
 * - Event notifications when data changes
 *
 * Cache duration: 5 minutes
 * Cache cleared on hard refresh (Ctrl+F5)
 */

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

class StateManager {
  constructor() {
    if (StateManager.instance) {
      return StateManager.instance;
    }

    this.state = {
      user: null,
      conversationId: null,
      currentMission: null,
      messages: [],
      todos: [],
      progressData: null
    };

    this.timestamps = {
      user: 0,
      messages: 0,
      todos: 0,
      progressData: 0
    };

    // Observer pattern - subscribers for each data type
    this.subscribers = {
      user: new Set(),
      messages: new Set(),
      todos: new Set(),
      progressData: new Set()
    };

    // Detect hard refresh
    this.detectHardRefresh();

    StateManager.instance = this;
  }

  /**
   * Detect hard refresh (Ctrl+F5) and clear cache
   */
  detectHardRefresh() {
    // Check if navigation type is reload
    if (performance.navigation && performance.navigation.type === 1) {
      // console.log('[StateManager] Hard refresh detected, clearing cache');
      this.clearAll();
    }

    // Modern API
    if (performance.getEntriesByType) {
      const navEntries = performance.getEntriesByType('navigation');
      if (navEntries.length > 0 && navEntries[0].type === 'reload') {
        // console.log('[StateManager] Hard refresh detected, clearing cache');
        this.clearAll();
      }
    }
  }

  /**
   * Check if cached data is still valid
   */
  isCacheValid(key) {
    const age = Date.now() - this.timestamps[key];
    return age < CACHE_DURATION;
  }

  /**
   * Get user info
   */
  getUser() {
    if (this.state.user && this.isCacheValid('user')) {
      // console.log('[StateManager] Using cached user data');
      return this.state.user;
    }
    return null;
  }

  /**
   * Set user info
   */
  setUser(user) {
    this.state.user = user;
    this.timestamps.user = Date.now();
    // console.log('[StateManager] User data cached');
  }

  /**
   * Get conversation ID
   */
  getConversationId() {
    return this.state.conversationId;
  }

  /**
   * Set conversation ID
   */
  setConversationId(id) {
    this.state.conversationId = id;
  }

  /**
   * Get current mission
   */
  getCurrentMission() {
    return this.state.currentMission;
  }

  /**
   * Set current mission
   */
  setCurrentMission(mission) {
    this.state.currentMission = mission;
  }

  /**
   * Get messages
   */
  getMessages() {
    if (this.state.messages.length > 0 && this.isCacheValid('messages')) {
      // console.log('[StateManager] Using cached messages');
      return this.state.messages;
    }
    return null;
  }

  /**
   * Set messages
   */
  setMessages(messages) {
    this.state.messages = messages;
    this.timestamps.messages = Date.now();
    // console.log('[StateManager] Messages cached:', messages.length);
  }

  /**
   * Add a message (optimistic update)
   */
  addMessage(message) {
    this.state.messages.push(message);
    this.timestamps.messages = Date.now();
  }

  /**
   * Update messages array
   */
  updateMessages(updateFn) {
    this.state.messages = updateFn(this.state.messages);
    this.timestamps.messages = Date.now();
  }

  /**
   * Get todos
   */
  getTodos() {
    if (this.state.todos.length > 0 && this.isCacheValid('todos')) {
      // console.log('[StateManager] Using cached todos');
      return this.state.todos;
    }
    return null;
  }

  /**
   * Set todos
   */
  setTodos(todos) {
    this.state.todos = todos;
    this.timestamps.todos = Date.now();
    // console.log('[StateManager] Todos cached:', todos.length, 'stages');

    // Notify subscribers
    this.notify('todos', todos);

    // Auto-invalidate progress data when todos change
    this.invalidate('progressData');
  }

  /**
   * Get progress data
   */
  getProgressData() {
    if (this.state.progressData && this.isCacheValid('progressData')) {
      // console.log('[StateManager] Using cached progress data');
      return this.state.progressData;
    }
    return null;
  }

  /**
   * Set progress data
   */
  setProgressData(data) {
    this.state.progressData = data;
    this.timestamps.progressData = Date.now();
    // console.log('[StateManager] Progress data cached');

    // Notify subscribers
    this.notify('progressData', data);
  }

  /**
   * Invalidate specific cache
   */
  invalidate(key) {
    this.timestamps[key] = 0;
    // console.log('[StateManager] Invalidated cache:', key);
  }

  /**
   * Clear all cached data
   */
  clearAll() {
    this.state = {
      user: null,
      conversationId: null,
      currentMission: null,
      messages: [],
      todos: [],
      progressData: null
    };

    this.timestamps = {
      user: 0,
      messages: 0,
      todos: 0,
      progressData: 0
    };

    // console.log('[StateManager] All cache cleared');
  }

  /**
   * Force refresh - invalidate all caches
   */
  forceRefresh() {
    Object.keys(this.timestamps).forEach(key => {
      this.timestamps[key] = 0;
    });
    // console.log('[StateManager] Force refresh - all caches invalidated');
  }

  /**
   * Subscribe to data changes
   * @param {string} key - Data key to subscribe to ('todos', 'progressData', 'messages', 'user')
   * @param {function} callback - Function to call when data changes
   * @returns {function} Unsubscribe function
   */
  subscribe(key, callback) {
    if (!this.subscribers[key]) {
      console.warn(`[StateManager] Invalid subscription key: ${key}`);
      return () => {};
    }

    this.subscribers[key].add(callback);
    // console.log(`[StateManager] Subscribed to ${key} changes (${this.subscribers[key].size} total subscribers)`);

    // Return unsubscribe function
    return () => {
      this.subscribers[key].delete(callback);
      // console.log(`[StateManager] Unsubscribed from ${key} changes (${this.subscribers[key].size} remaining subscribers)`);
    };
  }

  /**
   * Notify all subscribers of a data change
   * @param {string} key - Data key that changed
   * @param {*} newData - New data value
   */
  notify(key, newData) {
    if (!this.subscribers[key]) return;

    // console.log(`[StateManager] Notifying ${this.subscribers[key].size} subscribers of ${key} change`);
    this.subscribers[key].forEach(callback => {
      try {
        callback(newData);
      } catch (error) {
        console.error(`[StateManager] Error in ${key} subscriber callback:`, error);
      }
    });
  }
}

// Create singleton instance
const stateManager = new StateManager();

export default stateManager;
